/* ============================================================
   td-sticker-builder-v2.js
   Two independent bindings, no coupling to td-sticker-builder.js
   or td-sticker-preview.js:

   1) Gallery — vertical image carousel for [data-stk-gallery].
   2) Offer mode — single-offer LP binding for [data-stk-offer]:
      tier render + select, upload, proof-email modal, signed
      POST to the bridge. The v1 engine is NOT loaded on an
      offer page (the section liquid gates its script tag), so
      this binding owns the entire flow there.

      TWO offer pages share this one binding (2026-08-21). The
      section's offer_config_key setting names the block of
      td-sticker-config.json to read, and that key doubles as
      the spec flag the bridge branches on:

        offer_promo     /pages/stickers-offer
                        2″, free shipping, live preview modal
        deal_100_offer  /pages/100-sticker-deal
                        3″, charged shipping banded on the FINAL
                        quantity, checkout order bump, and NO
                        preview engine — the section omits both
                        the script and its host, so the upload
                        falls back to the inlined Cloudinary
                        uploader below (the same move
                        td-sticker-sample-blue.js made).

      Everything the deal adds is null-guarded off its own DOM
      hooks, so the 2″ page's behaviour is unchanged.
      See briefs/100-sticker-deal-page-2026-08-21.md.

   Multiple [data-stk-gallery] roots on a page are supported
   (each bound independently). [data-stk-offer] is expected to
   render once per page; the binding short-circuits if already
   bound (data-stk-offer-ready="1").

   See briefs/sticker-single-offer-lp-2026-06-22.md for offer-mode.
   ============================================================ */
(function () {
  'use strict';

  // ── Shape outline SVGs — copied verbatim from td-sticker-builder.js v1
  // (SHAPE_SVGS map). Inlined so the offer page doesn't have to load the v1
  // builder script just to render the shape icons. Two variants per shape:
  //   die_cut  = shape alone (cut goes through backing)
  //   kiss_cut = same shape nested inside a faint rounded square backing card
  // CurrentColor lets the .is-selected button tint the outline red. If the v1
  // map changes, mirror the change here (no shared module yet).
  var SHAPE_SVGS_OFFER = {
    die_cut: {
      custom_shape: '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M28 7 C37 3 47 7 47 17 C55 15 61 25 55 31 C61 37 57 47 48 46 C50 55 39 59 32 53 C25 60 15 56 16 47 C7 46 2 35 9 30 C4 21 11 13 19 16 C21 8 25 5 28 7 Z" stroke="currentColor" stroke-width="2.5" stroke-dasharray="5 4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      circle:       '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="32" cy="32" r="22" stroke="currentColor" stroke-width="2.5" stroke-dasharray="5 4" stroke-linecap="round"/></svg>',
      oval:         '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="32" cy="32" rx="26" ry="17" stroke="currentColor" stroke-width="2.5" stroke-dasharray="5 4" stroke-linecap="round"/></svg>',
      square:       '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="10" y="10" width="44" height="44" rx="4" stroke="currentColor" stroke-width="2.5" stroke-dasharray="5 4" stroke-linecap="round"/></svg>',
      rectangle:    '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="5" y="17" width="54" height="30" rx="4" stroke="currentColor" stroke-width="2.5" stroke-dasharray="5 4" stroke-linecap="round"/></svg>'
    },
    kiss_cut: {
      custom_shape: '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="58" height="58" rx="6" stroke="currentColor" stroke-width="1.5" stroke-opacity="0.45"/><path d="M28 17 C34 14 41 17 41 23 C46 22 50 29 46 32 C49 36 47 43 41 43 C42 49 34 51 30 47 C25 51 18 49 19 43 C13 42 11 35 16 32 C13 26 17 21 22 22 C24 17 26 16 28 17 Z" stroke="currentColor" stroke-width="2.5" stroke-dasharray="4 3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      circle:       '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="58" height="58" rx="6" stroke="currentColor" stroke-width="1.5" stroke-opacity="0.45"/><circle cx="32" cy="32" r="15" stroke="currentColor" stroke-width="2.5" stroke-dasharray="4 3" stroke-linecap="round"/></svg>',
      oval:         '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="58" height="58" rx="6" stroke="currentColor" stroke-width="1.5" stroke-opacity="0.45"/><ellipse cx="32" cy="32" rx="18" ry="12" stroke="currentColor" stroke-width="2.5" stroke-dasharray="4 3" stroke-linecap="round"/></svg>',
      square:       '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="58" height="58" rx="6" stroke="currentColor" stroke-width="1.5" stroke-opacity="0.45"/><rect x="18" y="18" width="28" height="28" rx="3" stroke="currentColor" stroke-width="2.5" stroke-dasharray="4 3" stroke-linecap="round"/></svg>',
      rectangle:    '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="58" height="58" rx="6" stroke="currentColor" stroke-width="1.5" stroke-opacity="0.45"/><rect x="10" y="22" width="44" height="20" rx="3" stroke="currentColor" stroke-width="2.5" stroke-dasharray="4 3" stroke-linecap="round"/></svg>'
    }
  };

  // Offer-mode shape → preview-engine shape mapping. The engine has 4 shape
  // primitives (custom, circle, square, rect); the offer adds 'oval' which
  // production cuts but the engine doesn't render — fall back to 'circle'
  // for the canvas. Production still cuts a true oval.
  var OFFER_SHAPE_TO_ENGINE = {
    custom_shape: 'custom',
    circle: 'circle',
    oval: 'circle',
    square: 'square',
    rectangle: 'rect'
  };

  var SHAPE_LABELS = { custom_shape: 'Custom Shape', circle: 'Circle', oval: 'Oval', square: 'Square', rectangle: 'Rectangle' };
  var CUT_LABELS = { die_cut: 'Die Cut', kiss_cut: 'Kiss Cut' };
  var BORDER_LABELS = { thin: 'Thin', regular: 'Regular', wide: 'Wide' };
  // V5 — Border Type → preview-engine bleed values. The engine exposes Thin/
  // Normal/Wide as 0.025/0.05/0.08 on data-stk-preview-bleed; Regular maps to
  // the engine default 'normal' (0.05).
  var BORDER_TO_ENGINE_BLEED = { thin: '0.025', regular: '0.05', wide: '0.08' };

  // Money for prices read straight out of the config. JSON has no decimal type, so
  // an anchor written 67.50 parses to 67.5 and naive concatenation renders "$67.5".
  // Trailing ".00" is trimmed so the whole-dollar anchors the 2″ offer has always
  // carried ($61 / $74 since the 2026-08-27 increase) keep rendering exactly as they
  // do today. The 67.50 above is a parsing illustration, NOT a live price.
  function fmtMoney(n) {
    var v = Number(n);
    if (!isFinite(v)) return '';
    return v.toFixed(2).replace(/\.00$/, '');
  }

  var round2 = function (n) { return Math.round(n * 100) / 100; };

  // Inlined Cloudinary uploader — the fallback for pages that do NOT load
  // td-sticker-preview.js. The deal variant suppresses the preview engine
  // (brief §5), and the order-time artwork capture used to come from that engine's
  // uploadArtwork(). Ported from td-sticker-sample-blue.js, which solved exactly
  // this when it dropped the engine: same cloud, same unsigned preset, same folder
  // the capture has always written to. When the engine IS present (the 2″ offer
  // page) its own uploader is still used, so that path is byte-unchanged.
  var CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dsbz1mtmr/image/upload';
  var CLOUDINARY_PRESET = 'tapedudes_unsigned';
  /* ── SVG normalisation ────────────────────────────────────────────────
     Cloudinary sniffs an SVG's format from its FIRST BYTES. A file that does
     not begin with an XML declaration sniffs as `raw`, and the unsigned preset
     rejects it: 400 "Raw file format svg not allowed" — measured 2026-08-31 on
     auto/upload, image/upload and raw/upload alike. Illustrator "Export As" and
     Figma both write SVGs without the declaration. Order #2869 lost its artwork
     this way. A BOM is tolerated but leading whitespace is NOT, so the
     declaration has to end up at byte 0.
     Fails open: any unexpected file, or any read error, uploads unchanged. */
  var TD_XML_DECL = '<?xml version="1.0" encoding="UTF-8"?>\n';

  function normalizeSvg(file) {
    var name = file && file.name ? String(file.name).toLowerCase() : '';
    var looksSvg = !!file && (file.type === 'image/svg+xml' || /\.svg$/.test(name));
    if (!looksSvg || !file.slice || !file.slice(0, 1).arrayBuffer) return Promise.resolve(file);

    return file.slice(0, 1024).arrayBuffer().then(function (buf) {
      var b = new Uint8Array(buf), i = 0;
      if (b[0] === 0xEF && b[1] === 0xBB && b[2] === 0xBF) i = 3;                 // BOM
      while (i < b.length && (b[i] === 0x20 || b[i] === 0x09 || b[i] === 0x0A || b[i] === 0x0D)) i++;
      var head = new TextDecoder().decode(b.subarray(i));

      // Already declared and already at byte 0 — leave the bytes alone.
      if (/^<\?xml[\s?]/.test(head)) return i === 0 ? file : new File([file.slice(i)], file.name, { type: 'image/svg+xml' });
      // Named .svg but not SVG markup — never rewrite it, that would corrupt it.
      if (!/<svg[\s>]/i.test(head)) return file;
      return new File([TD_XML_DECL, file.slice(i)], file.name, { type: 'image/svg+xml' });
    })['catch'](function () { return file; });
  }

  function uploadToCloudinary(file, folder) {
    return normalizeSvg(file).then(function (f) {
      var fd = new FormData();
      fd.append('file', f);
      fd.append('upload_preset', CLOUDINARY_PRESET);
      if (folder) fd.append('folder', folder);
      return fetch(CLOUDINARY_URL, { method: 'POST', body: fd })
        .then(function (res) { if (!res.ok) throw new Error('upload HTTP ' + res.status); return res.json(); })
        .then(function (j) { if (!j || !j.public_id) throw new Error('no public_id'); return { public_id: j.public_id, secure_url: j.secure_url }; });
    });
  }

  function initGallery(gallery) {
    if (!gallery || gallery.dataset.stkGalleryReady === '1') return;

    var slides = Array.prototype.slice.call(
      gallery.querySelectorAll('.stk__gallery-slide')
    );
    var dots = Array.prototype.slice.call(
      gallery.querySelectorAll('.stk__gallery-dot')
    );

    if (slides.length < 2) {
      // Single image (or none) — nothing to paginate.
      gallery.dataset.stkGalleryReady = '1';
      return;
    }

    var current = 0;
    slides.forEach(function (slide, i) {
      if (slide.classList.contains('is-active')) current = i;
    });

    function show(idx) {
      if (idx < 0) idx = slides.length - 1;
      if (idx >= slides.length) idx = 0;
      if (idx === current) return;
      slides[current].classList.remove('is-active');
      slides[idx].classList.add('is-active');
      if (dots[current]) {
        dots[current].classList.remove('is-active');
        dots[current].setAttribute('aria-selected', 'false');
      }
      if (dots[idx]) {
        dots[idx].classList.add('is-active');
        dots[idx].setAttribute('aria-selected', 'true');
      }
      current = idx;
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { show(i); });
    });

    var prevBtn = gallery.querySelector('[data-stk-gallery-prev]');
    var nextBtn = gallery.querySelector('[data-stk-gallery-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { show(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { show(current + 1); });

    gallery.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        show(current + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        show(current - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        show(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        show(slides.length - 1);
      }
    });

    gallery.dataset.stkGalleryReady = '1';
  }

  // ──────────────────────────────────────────────
  // OFFER MODE binding
  // ──────────────────────────────────────────────
  function initOffer(root) {
    if (!root || root.dataset.stkOfferReady === '1') return;

    var configUrl = root.dataset.configUrl;
    var endpoint = root.dataset.checkoutEndpoint;
    var nonce = root.dataset.signingNonce;
    var variantId = root.dataset.variantId;
    var defaultTier = String(root.dataset.defaultTier || '100');
    // ONE binding, TWO offer pages (2026-08-21). The section setting names the
    // config block; the block's own key doubles as the spec flag the bridge
    // branches on (spec.offer_promo / spec.deal_100_offer), so there is exactly
    // one string to keep in step between the page, the config and the bridge.
    var offerKey = String(root.dataset.offerConfigKey || 'offer_promo');

    var tiersWrap = root.querySelector('[data-stk-offer-tiers]');
    var shapeGridEl = root.querySelector('[data-stk-offer-shape-grid]');
    var cutToggleEl = root.querySelector('[data-stk-offer-cut-toggle]');
    var materialGridEl = root.querySelector('[data-stk-offer-material-grid]');
    var speedToggleEl = root.querySelector('[data-stk-offer-speed-toggle]');
    var borderToggleEl = root.querySelector('[data-stk-offer-border-toggle]');
    var notesEl = root.querySelector('[data-stk-offer-notes]');
    var notesStatusEl = root.querySelector('[data-stk-offer-notes-status]');
    var totalAmountEl = root.querySelector('[data-stk-offer-total-amount]');
    var totalBreakdownEl = root.querySelector('[data-stk-offer-total-breakdown]');
    var wygQtyLine = root.querySelector('[data-stk-offer-wyg-qty-line]');
    var fileInput = root.querySelector('[data-stk-offer-file-input]');
    var uploadLabel = root.querySelector('[data-stk-offer-upload-label]');
    var uploadPreview = root.querySelector('[data-stk-offer-upload-preview]');
    var thumbImg = root.querySelector('[data-stk-offer-upload-thumb-img]');
    var thumbEmpty = root.querySelector('[data-stk-offer-upload-thumb-empty]');
    var filenameEl = root.querySelector('[data-stk-offer-filename]');
    var editBtn = root.querySelector('[data-stk-offer-upload-edit]');
    var removeBtn = root.querySelector('[data-stk-offer-remove]');
    var cta = root.querySelector('[data-stk-offer-cta]');
    var ctaError = root.querySelector('[data-stk-offer-cta-error]');

    // Charged-shipping rows + order-bump interstitial. All absent on the 2″ offer
    // page, and every consumer below null-guards, so that page's behaviour is
    // unchanged by their existence here.
    var shipNoteEl = root.querySelector('[data-stk-offer-ship-note]');

    var bumpModal = root.querySelector('[data-stk-offer-bump-modal]');
    var bumpHeadlineEl = root.querySelector('[data-stk-offer-bump-headline]');
    var bumpSubLeadEl = root.querySelector('[data-stk-offer-bump-sub-lead]');
    var bumpSubBoldEl = root.querySelector('[data-stk-offer-bump-sub-bold]');
    var bumpNow = {
      qty:   root.querySelector('[data-stk-offer-bump-now-qty]'),
      note:  root.querySelector('[data-stk-offer-bump-now-note]'),
      total: root.querySelector('[data-stk-offer-bump-now-total]'),
      each:  root.querySelector('[data-stk-offer-bump-now-each]')
    };
    var bumpUp = {
      qty:   root.querySelector('[data-stk-offer-bump-up-qty]'),
      note:  root.querySelector('[data-stk-offer-bump-up-note]'),
      total: root.querySelector('[data-stk-offer-bump-up-total]'),
      each:  root.querySelector('[data-stk-offer-bump-up-each]')
    };
    var bumpShipEl = root.querySelector('[data-stk-offer-bump-ship]');
    var bumpAcceptBtn = root.querySelector('[data-stk-offer-bump-accept]');
    var bumpDeclineBtn = root.querySelector('[data-stk-offer-bump-decline]');
    var bumpError = root.querySelector('[data-stk-offer-bump-error]');
    var bumpLastFocus = null;

    // Sticky bottom bar (2026-06-24) — proxy only. Mirrors cta's
    // readiness/label/disabled and fires cta.click() on tap. Visibility is
    // driven by syncStickyBar(); ctaInView is set by the IntersectionObserver
    // (assume in view until proven otherwise so the bar doesn't flash before
    // the observer's first callback).
    var stickyBar    = root.querySelector('[data-stk-offer-sticky]');
    var stickyThumb  = root.querySelector('[data-stk-offer-sticky-thumb]');
    var stickyQty    = root.querySelector('[data-stk-offer-sticky-qty]');
    var stickyTotal  = root.querySelector('[data-stk-offer-sticky-total]');
    var stickyBtn    = root.querySelector('[data-stk-offer-sticky-btn]');
    var stickyBtnTxt = root.querySelector('[data-stk-offer-sticky-btn-text]');
    var ctaInView = true;
    var previewModalOpen = false;
    var bumpModalOpen = false;

    // Preview-engine hosts (V3 Item 1) — modal opens on upload, snapshots the
    // canvas into thumbImg on close. Engine binds inside previewPane via the
    // data-stk-preview-* hooks declared in the section markup.
    var previewModal = root.querySelector('[data-stk-offer-preview-modal]');
    var previewModalClose = root.querySelector('[data-stk-offer-preview-modal-close]');
    var previewModalDone = root.querySelector('[data-stk-offer-preview-modal-done]');
    var previewPane = root.querySelector('[data-stk-preview-pane]');
    var previewCanvas = root.querySelector('[data-stk-preview-canvas]');
    var previewControls = root.querySelector('[data-stk-preview-controls]');
    var previewController = null;
    var previewModalLastFocus = null;

    if (!tiersWrap || !fileInput || !cta) return;

    // ASSET_BASE — same dir as td-sticker-config.json; used to build material
    // PNG icon URLs (configurator pattern: td-sticker-material-vinyl-{matte,gloss}.png
    // with td-sticker-icon-material-*.svg as the onerror fallback).
    var ASSET_BASE = (configUrl || '').split('td-sticker-config.json')[0];

    var state = {
      config: null,
      selectedTier: null,
      selectedShape: null,
      selectedCutType: null,
      selectedFinish: null,
      selectedSpeed: null,
      selectedBorder: null,
      notes: '',
      artwork_filename: '',
      artwork_file: null,
      submitting: false,
      preview_meta: null,
      // Order bump (deal variant). `bump` is the accepted ladder entry — it moves
      // the ORDER onto a bigger rung; the extras it ships are a fulfilment bonus
      // the bridge derives from config, never a client-sent count.
      bump: null,
      pendingBump: null,
      bumpDecided: false,
      objectUrl: null
    };

    // Preview engine — defensive bind: only if engine script loaded AND pane
    // is present. Material is locked to vinyl per the offer spec; no glitter
    // assets needed (those are only for the holographic/glitter/chrome SKUs).
    // The engine auto-hides the round + fit controls when shape='custom'
    // (its default), so the offer controls block renders Border + Background
    // only with no extra wiring needed here.
    if (previewPane && window.TDStickerPreview) {
      try {
        previewController = window.TDStickerPreview.init({
          pane: previewPane,
          controls: previewControls,
          material: 'vinyl',
          onStateChange: function (meta) { state.preview_meta = meta; }
        });
      } catch (err) {
        console.warn('[stk-offer] preview init failed', err);
        previewController = null;
      }
    }

    if (!configUrl) return;
    fetch(configUrl)
      .then(function (r) { return r.json(); })
      .then(function (cfg) {
        state.config = cfg;
        var offer = cfg && cfg[offerKey];
        if (!offer || !offer.tiers) return;
        // Master kill switch (config only, no deploy). The bridge 400s every spec on
        // this path once it flips, so the page must not keep offering a checkout it
        // can no longer complete: swap the body for the offer-ended notice and stop.
        // Nothing below this line runs, so no tier, price or CTA is ever rendered.
        if (offer.enabled === false) { showOfferEnded(); return; }
        renderTiers(offer);
        renderShape(offer);
        renderCutType(offer);
        renderMaterial(offer);
        renderSpeed(offer);
        renderBorder(offer);

        var startTier = offer.tiers[defaultTier] ? defaultTier : Object.keys(offer.tiers)[0];
        if (startTier) selectTier(startTier);

        // Defaults — pre-select the engine starting state and the user-visible
        // pills. Order matters: cut_type before shape so the shape icons render
        // in the right variant (die_cut vs kiss_cut backing card).
        var cutOpts = Array.isArray(offer.cut_type_options) ? offer.cut_type_options : [];
        var startCut = (offer.default_cut_type && cutOpts.indexOf(offer.default_cut_type) >= 0)
          ? offer.default_cut_type : (cutOpts[0] || 'die_cut');
        selectCutType(startCut);

        var shapeOpts = Array.isArray(offer.shape_options) ? offer.shape_options : [];
        var startShape = (offer.default_shape && shapeOpts.indexOf(offer.default_shape) >= 0)
          ? offer.default_shape : (shapeOpts[0] || 'custom_shape');
        selectShape(startShape);

        var finishOpts = Array.isArray(offer.finish_options) ? offer.finish_options : [];
        var startFinish = (offer.default_finish && finishOpts.indexOf(offer.default_finish) >= 0)
          ? offer.default_finish : (finishOpts[0] || 'matte');
        selectFinish(startFinish);

        var speedKeys = offer.production_speed ? Object.keys(offer.production_speed) : [];
        var startSpeed = (offer.default_speed && speedKeys.indexOf(offer.default_speed) >= 0)
          ? offer.default_speed : (speedKeys[0] || 'regular');
        selectSpeed(startSpeed);

        var borderOpts = Array.isArray(offer.border_options) ? offer.border_options : [];
        var startBorder = (offer.default_border && borderOpts.indexOf(offer.default_border) >= 0)
          ? offer.default_border : (borderOpts[0] || 'regular');
        selectBorder(startBorder);

        refreshTotal();
      })
      .catch(function (err) { /* swallow — CTA stays disabled if config fails */ console.warn('[stk-offer] config fetch failed', err); });

    // Offer withdrawn from config. Leaves the hero and the browse-more escape box
    // standing — a cold visitor who clicked an ad that outlived the deal should land
    // somewhere that explains itself and sells something, not on a broken buy box.
    function showOfferEnded() {
      var ended = root.querySelector('[data-stk-offer-ended]');
      if (!ended) return;
      root.classList.add('stk-offer--ended');
      ended.hidden = false;
      if (cta) { cta.disabled = true; cta.hidden = true; }
    }

    function renderTiers(offer) {
      var qtys = Object.keys(offer.tiers).sort(function (a, b) { return Number(b) - Number(a); });
      var html = '';
      qtys.forEach(function (qty) {
        var t = offer.tiers[qty];
        var savings = Math.round((1 - t.promo_price / t.anchor_price) * 100);
        var perEach = (t.promo_price / Number(qty)).toFixed(2);
        var isHero = qty === String(offer.default_tier);
        html += '<button type="button" class="stk-offer__tier' + (isHero ? ' stk-offer__tier--hero' : '') + '" data-stk-offer-tier="' + qty + '" role="radio" aria-checked="false">'
          + (isHero ? '<span class="stk-offer__tier-badge">Most Popular</span>' : '')
          + '<span class="stk-offer__tier-qty">' + qty + ' stickers</span>'
          + '<span class="stk-offer__tier-price">$' + fmtMoney(t.promo_price) + '</span>'
          + '<span class="stk-offer__tier-meta"><span class="stk-offer__tier-strike">$' + fmtMoney(t.anchor_price) + '</span> <span class="stk-offer__tier-off">' + savings + '% OFF</span></span>'
          + '<span class="stk-offer__tier-pereach">$' + perEach + '/each</span>'
          + '</button>';
      });
      tiersWrap.innerHTML = html;
      Array.prototype.slice.call(tiersWrap.querySelectorAll('[data-stk-offer-tier]')).forEach(function (btn) {
        btn.addEventListener('click', function () { selectTier(btn.getAttribute('data-stk-offer-tier')); });
      });
    }

    function selectTier(qty) {
      state.selectedTier = qty;
      Array.prototype.slice.call(tiersWrap.querySelectorAll('[data-stk-offer-tier]')).forEach(function (btn) {
        var on = btn.getAttribute('data-stk-offer-tier') === qty;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      refreshWhatYouGet();
      refreshTotal();
      refreshCta();
    }

    // ── SHAPE selector ──────────────────────────────────
    function renderShape(offer) {
      if (!shapeGridEl) return;
      var opts = Array.isArray(offer.shape_options) ? offer.shape_options : [];
      var html = '';
      opts.forEach(function (shape) {
        var label = SHAPE_LABELS[shape] || shape;
        // Initial variant = die_cut. Re-rendered to kiss_cut variant when the
        // cut-type pill flips, via rerenderShapeIcons() — same per-card pattern
        // the v1 PDP uses (state.cut_type drives SHAPE_SVGS[variant][shape]).
        var svg = (SHAPE_SVGS_OFFER.die_cut && SHAPE_SVGS_OFFER.die_cut[shape]) || '';
        html += '<button type="button" class="stk-offer__shape-btn" data-stk-offer-shape="' + shape + '" role="radio" aria-checked="false" aria-label="' + label + '">'
          + '<span class="stk-offer__shape-icon" aria-hidden="true">' + svg + '</span>'
          + '<span class="stk-offer__shape-name">' + label + '</span>'
          + '</button>';
      });
      shapeGridEl.innerHTML = html;
      Array.prototype.slice.call(shapeGridEl.querySelectorAll('[data-stk-offer-shape]')).forEach(function (btn) {
        btn.addEventListener('click', function () { selectShape(btn.getAttribute('data-stk-offer-shape')); });
      });
    }
    function selectShape(shape) {
      state.selectedShape = shape;
      if (shapeGridEl) {
        Array.prototype.slice.call(shapeGridEl.querySelectorAll('[data-stk-offer-shape]')).forEach(function (btn) {
          var on = btn.getAttribute('data-stk-offer-shape') === shape;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }
      syncPreviewShape();
      // Shape has no price impact (V4: all shape modifiers are 1.0), so no
      // refreshTotal() call here. Future-proof: if shape ever gets a modifier,
      // add it to the bridge formula + computeTotal() and refresh here.
    }
    function syncPreviewShape() {
      if (!previewControls || !state.selectedShape) return;
      var engineShape = OFFER_SHAPE_TO_ENGINE[state.selectedShape] || 'custom';
      var row = previewControls.querySelector('[data-stk-preview-shape]');
      if (!row) return;
      var target = row.querySelector('[data-v="' + engineShape + '"]');
      if (target && !target.classList.contains('is-selected')) {
        target.click(); // engine's bindOptRow updates state.controls.shape + re-renders if a file is loaded
      }
    }

    // ── CUT TYPE toggle ─────────────────────────────────
    function renderCutType(offer) {
      if (!cutToggleEl) return;
      var opts = Array.isArray(offer.cut_type_options) ? offer.cut_type_options : [];
      var mods = offer.cut_type_modifiers || {};
      var html = '';
      opts.forEach(function (cut) {
        var label = CUT_LABELS[cut] || cut;
        var mod = mods[cut];
        var pct = (typeof mod === 'number' && mod > 1) ? Math.round((mod - 1) * 100) : 0;
        var badge = pct ? '<span class="stk-offer__pct-badge">+' + pct + '%</span>' : '';
        html += '<button type="button" class="stk-offer__cut-btn" data-stk-offer-cut="' + cut + '" role="radio" aria-checked="false">'
          + '<span class="stk-offer__cut-name">' + label + '</span>' + badge
          + '</button>';
      });
      cutToggleEl.innerHTML = html;
      Array.prototype.slice.call(cutToggleEl.querySelectorAll('[data-stk-offer-cut]')).forEach(function (btn) {
        btn.addEventListener('click', function () { selectCutType(btn.getAttribute('data-stk-offer-cut')); });
      });
    }
    function selectCutType(cut) {
      state.selectedCutType = cut;
      if (cutToggleEl) {
        Array.prototype.slice.call(cutToggleEl.querySelectorAll('[data-stk-offer-cut]')).forEach(function (btn) {
          var on = btn.getAttribute('data-stk-offer-cut') === cut;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }
      rerenderShapeIcons();
      refreshTotal();
    }
    function rerenderShapeIcons() {
      if (!shapeGridEl) return;
      var variants = (state.selectedCutType === 'kiss_cut') ? SHAPE_SVGS_OFFER.kiss_cut : SHAPE_SVGS_OFFER.die_cut;
      Array.prototype.slice.call(shapeGridEl.querySelectorAll('[data-stk-offer-shape]')).forEach(function (btn) {
        var shape = btn.getAttribute('data-stk-offer-shape');
        var iconEl = btn.querySelector('.stk-offer__shape-icon');
        if (iconEl && variants[shape]) iconEl.innerHTML = variants[shape];
      });
    }

    // ── MATERIAL grid (Matte / Gloss + PNG icon + +10% badge) ──
    function renderMaterial(offer) {
      if (!materialGridEl) return;
      var opts = Array.isArray(offer.finish_options) ? offer.finish_options : [];
      var mods = offer.finish_modifiers || {};
      var html = '';
      opts.forEach(function (finish) {
        var label = finish.charAt(0).toUpperCase() + finish.slice(1);
        var mod = mods[finish];
        var pct = (typeof mod === 'number' && mod > 1) ? Math.round((mod - 1) * 100) : 0;
        var badge = pct ? '<span class="stk-offer__pct-badge">+' + pct + '%</span>' : '';
        // Match configurator pattern: try per-product PNG first, fall back to
        // generic SVG via onerror. ASSET_BASE comes from the configUrl host.
        var pngUrl = ASSET_BASE + 'td-sticker-material-vinyl-' + finish + '.png';
        var svgUrl = ASSET_BASE + 'td-sticker-icon-material-' + finish + '.svg';
        html += '<button type="button" class="stk-offer__material-btn" data-stk-offer-material="' + finish + '" role="radio" aria-checked="false" aria-label="' + label + '">'
          + '<img class="stk-offer__material-icon" alt="" src="' + pngUrl + '" onerror="this.onerror=null;this.src=\'' + svgUrl + '\'">'
          + '<span class="stk-offer__material-name">' + label + '</span>' + badge
          + '</button>';
      });
      materialGridEl.innerHTML = html;
      Array.prototype.slice.call(materialGridEl.querySelectorAll('[data-stk-offer-material]')).forEach(function (btn) {
        btn.addEventListener('click', function () { selectFinish(btn.getAttribute('data-stk-offer-material')); });
      });
    }
    function selectFinish(finish) {
      state.selectedFinish = finish;
      if (materialGridEl) {
        Array.prototype.slice.call(materialGridEl.querySelectorAll('[data-stk-offer-material]')).forEach(function (btn) {
          var on = btn.getAttribute('data-stk-offer-material') === finish;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }
      refreshTotal();
    }

    // ── PRODUCTION SPEED toggle ─────────────────────────
    function renderSpeed(offer) {
      if (!speedToggleEl || !offer.production_speed) return;
      var keys = Object.keys(offer.production_speed);
      var html = '';
      keys.forEach(function (key) {
        var entry = offer.production_speed[key] || {};
        var addonText = entry.addon > 0 ? '+$' + Number(entry.addon).toFixed(2) : '+$0';
        html += '<button type="button" class="stk-offer__speed-btn" data-stk-offer-speed="' + key + '" role="radio" aria-checked="false">'
          + '<span class="stk-offer__speed-row">'
          +   '<span class="stk-offer__speed-name">' + (entry.label || key) + '</span>'
          +   '<span class="stk-offer__speed-addon">' + addonText + '</span>'
          + '</span>'
          + '<span class="stk-offer__speed-sub">' + (entry.sub || '') + '</span>'
          + '</button>';
      });
      speedToggleEl.innerHTML = html;
      Array.prototype.slice.call(speedToggleEl.querySelectorAll('[data-stk-offer-speed]')).forEach(function (btn) {
        btn.addEventListener('click', function () { selectSpeed(btn.getAttribute('data-stk-offer-speed')); });
      });
    }
    function selectSpeed(speed) {
      state.selectedSpeed = speed;
      if (speedToggleEl) {
        Array.prototype.slice.call(speedToggleEl.querySelectorAll('[data-stk-offer-speed]')).forEach(function (btn) {
          var on = btn.getAttribute('data-stk-offer-speed') === speed;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }
      refreshTotal();
    }

    // ── BORDER TYPE — V5 captured selector (no price impact) ─────────
    // Drives the preview engine's bleed via syncPreviewBleed() so the modal
    // and the configurator selector can never disagree. The in-modal Border
    // row is hidden in markup so this selector is the single source of truth.
    function renderBorder(offer) {
      if (!borderToggleEl) return;
      var opts = Array.isArray(offer.border_options) ? offer.border_options : [];
      var html = '';
      opts.forEach(function (border) {
        var label = BORDER_LABELS[border] || (border.charAt(0).toUpperCase() + border.slice(1));
        html += '<button type="button" class="stk-offer__border-btn" data-stk-offer-border="' + border + '" role="radio" aria-checked="false">'
          + '<span class="stk-offer__border-name">' + label + '</span>'
          + '</button>';
      });
      borderToggleEl.innerHTML = html;
      Array.prototype.slice.call(borderToggleEl.querySelectorAll('[data-stk-offer-border]')).forEach(function (btn) {
        btn.addEventListener('click', function () { selectBorder(btn.getAttribute('data-stk-offer-border')); });
      });
    }
    function selectBorder(border) {
      state.selectedBorder = border;
      if (borderToggleEl) {
        Array.prototype.slice.call(borderToggleEl.querySelectorAll('[data-stk-offer-border]')).forEach(function (btn) {
          var on = btn.getAttribute('data-stk-offer-border') === border;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }
      syncPreviewBleed();
      // No price impact (V5). Border choice is captured + stamped on the
      // order via the bridge's buildStickerProperties() "Border Type" line.
    }
    function syncPreviewBleed() {
      if (!previewControls || !state.selectedBorder) return;
      var engineBleed = BORDER_TO_ENGINE_BLEED[state.selectedBorder] || '0.05';
      var row = previewControls.querySelector('[data-stk-preview-bleed]');
      if (!row) return;
      var target = row.querySelector('[data-v="' + engineBleed + '"]');
      if (target && !target.classList.contains('is-selected')) {
        target.click(); // engine's bindOptRow updates state.controls.bleed + re-renders
      }
    }

    // ── Live total — mirror of the bridge's computeStickerOfferPrice() ──
    // Returns { total, breakdown } or null while state is incomplete. Formula
    // must stay byte-identical to the bridge (V4 brief §3): total = promo
    // × matMod × cutMod + speedAddon, rounded to 2dp. The bridge re-computes
    // server-side; this is the user-facing display only.
    // `bump` (optional) prices the ACCEPTED ladder entry instead of the plain rung:
    // base becomes tiers[from].promo_price + addon, and the modifiers compound onto
    // it exactly as they do on an unbumped order — which is why the modal's "+$X"
    // must be computed, never hardcoded (gloss and kiss-cut each move it).
    // Byte-identical to the bridge's computeStickerDealPrice() / computeStickerOfferPrice().
    function computeOfferTotal(bump) {
      var offer = state.config && state.config[offerKey];
      if (!offer || !offer.tiers || !state.selectedTier) return null;
      var tier = offer.tiers[state.selectedTier];
      if (!tier) return null;
      var promo = bump ? round2(Number(tier.promo_price) + Number(bump.addon)) : Number(tier.promo_price);
      var matMod = offer.finish_modifiers && offer.finish_modifiers[state.selectedFinish];
      if (typeof matMod !== 'number') return null;
      var cutMod = offer.cut_type_modifiers && offer.cut_type_modifiers[state.selectedCutType];
      if (typeof cutMod !== 'number') return null;
      var speedEntry = offer.production_speed && offer.production_speed[state.selectedSpeed];
      if (!speedEntry || typeof speedEntry.addon !== 'number') return null;
      var raw = promo * matMod * cutMod + speedEntry.addon;
      var total = Math.round(raw * 100) / 100;
      // Itemized breakdown — only non-zero contributions. matAdd applies matMod
      // first, then cutMod compounds on top (matches the multiplicative chain
      // total = promo × matMod × cutMod so the parts sum back to the total).
      var parts = [];
      if (matMod > 1) {
        var matAdd = Math.round((promo * (matMod - 1) * cutMod) * 100) / 100;
        parts.push('Gloss +$' + matAdd.toFixed(2));
      }
      if (cutMod > 1) {
        var cutAdd = Math.round((promo * (cutMod - 1)) * 100) / 100;
        parts.push('Kiss Cut +$' + cutAdd.toFixed(2));
      }
      if (speedEntry.addon > 0) {
        parts.push((speedEntry.label || 'Skip the Line') + ' +$' + Number(speedEntry.addon).toFixed(2));
      }
      return { total: total, breakdown: parts, basePrice: promo };
    }

    // ── Charged shipping (deal variant) ─────────────────────
    // Mirror of the bridge's resolveDealShipping(): same band table, same
    // first-match-on-ascending-max_qty rule, resolved against the same FINAL
    // quantity (rung + any bump bonus). The page quotes it; the bridge charges it
    // — and the bridge resolves it again server-side, so nothing here is trusted.
    // Returns null when the selected block has no `shipping` (i.e. the 2″ offer),
    // which is what keeps that page's single-total layout untouched.
    function resolveShipping(finalQty) {
      var offer = state.config && state.config[offerKey];
      var cfg = offer && offer.shipping;
      if (!cfg) return null;
      // Mirror of the bridge's gate: carrier mode has no price to show, so the page
      // must not invent one. Only flat mode yields a number.
      if (String(cfg.mode || 'carrier').toLowerCase() !== 'flat') return null;
      var bands = (Array.isArray(cfg.bands) ? cfg.bands.slice() : [])
        .filter(function (b) { return b && Number(b.max_qty) > 0 && isFinite(Number(b.price)); })
        .sort(function (a, b) { return Number(a.max_qty) - Number(b.max_qty); });
      var hit = null;
      for (var i = 0; i < bands.length; i++) {
        if (finalQty <= Number(bands[i].max_qty)) { hit = bands[i]; break; }
      }
      var price = Number(hit ? hit.price : cfg.fallback_price);
      if (!isFinite(price) || price <= 0) return null;
      return { title: String(cfg.title || 'Shipping'), price: price };
    }

    // ── Order bump ladder (deal variant) ────────────────────
    // One rung of deal_100_offer.bump, validated the same way the bridge's
    // resolveDealBump() validates it — the rung must be a real tier and the entry a
    // real object (the map also holds _comment/_formula strings). Returns null when
    // the selected config block has no ladder, which is what leaves the 2″ offer's
    // CTA going straight to checkout as it does today.
    // "31¢ each" is the number a business owner actually responds to, and it was
    // nowhere on the old modal. Falls back to dollars above 99¢ so the label can
    // never render something like "176¢ each" if a rung is ever repriced upward.
    function perStickerLabel(total, qty) {
      if (!(qty > 0) || !isFinite(total)) return '';
      var cents = Math.round((total / qty) * 100);
      return cents >= 100 ? '$' + (cents / 100).toFixed(2) + ' each' : cents + '¢ each';
    }

    function bumpEntryFor(fromQty) {
      var offer = state.config && state.config[offerKey];
      if (!offer || offer.enabled === false) return null;
      var map = offer && offer.bump;
      if (!map || !offer.tiers) return null;
      // Config-only kill switch, mirroring the bridge's resolveDealBump(). With the
      // bump withdrawn the CTA goes straight to checkout, which is exactly what an
      // offer_promo page does — no dead modal, no rejected spec.
      if (map.enabled === false) return null;
      var key = String(fromQty);
      var e = map[key];
      if (!e || typeof e !== 'object') return null;
      if (!offer.tiers[key]) return null;
      var toQty = Number(e.to_qty);
      var addon = Number(e.addon);
      if (!(toQty > 0) || !(addon > 0)) return null;
      return { from: Number(fromQty), toQty: toQty, addon: addon, bonus: Number(e.bonus) || 0 };
    }
    function refreshTotal() {
      var info = computeOfferTotal(state.bump);
      if (!info) {
        if (totalAmountEl) totalAmountEl.textContent = '$—';
        if (totalBreakdownEl) totalBreakdownEl.textContent = '';
      } else {
        if (totalAmountEl) totalAmountEl.textContent = '$' + info.total.toFixed(2);
        if (totalBreakdownEl) {
          totalBreakdownEl.textContent = info.breakdown.length
            ? 'Base $' + info.basePrice.toFixed(2) + ' · ' + info.breakdown.join(' · ')
            : '';
        }
      }
      // No running total on this variant any more. Shipping is carrier-calculated
      // from the line's weight (2026-08-21), so the real figure does not exist until
      // Shopify has an address — and a guessed one that disagrees with checkout is
      // worse than none. The Liquid microcopy already says "Shipping calculated at
      // checkout"; only in the FLAT-mode rollback, where the price IS knowable, do
      // we append it, so a config revert restores an honest number without a deploy.
      if (shipNoteEl) {
        var ship = resolveShipping(finalQty());
        shipNoteEl.textContent = ship
          ? 'Shipping ' + '$' + ship.price.toFixed(2) + ' — added at checkout.'
          : 'Shipping calculated at checkout.';
      }
      syncStickyBar();
    }

    // What actually SHIPS — the billed rung plus any accepted bump bonus. This is
    // the quantity the shipping band is resolved against, on both sides.
    function finalQty() {
      var qty = state.bump ? state.bump.toQty : Number(state.selectedTier || 0);
      return qty + (state.bump ? state.bump.bonus : 0);
    }

    // Sticky bar — mirror cta + decide visibility. Single source of truth for
    // both label and disabled is the real cta; the bar never re-derives the
    // ready state (cta.disabled already centralizes it in refreshCta).
    function syncStickyBar() {
      if (!stickyBar) return;
      var artworkPresent = !!state.artwork_filename;
      var show = artworkPresent && !ctaInView && !previewModalOpen && !bumpModalOpen;
      if (stickyQty && state.selectedTier) {
        stickyQty.textContent = state.selectedTier + ' stickers';
      }
      if (stickyTotal && totalAmountEl) {
        // The sticker total, never a grand total — postage is carrier-quoted at
        // checkout and unknown here. Strip trailing ".00" so the bar reads "$39"
        // not "$39.00" — matches the visual weight of the hero anchor price.
        stickyTotal.textContent = totalAmountEl.textContent.replace(/\.00$/, '');
      }
      if (stickyBtn) {
        stickyBtn.disabled = cta.disabled;
      }
      if (stickyBtnTxt) {
        // Mirror the real cta's label so the bar reflects "Creating checkout…"
        // during submit. Pre-upload, cta's "Upload your artwork…" never reaches
        // the bar (show=false), so the user-visible label is always either
        // "Place My Order" or "Creating checkout…".
        stickyBtnTxt.textContent = cta.textContent;
      }
      if (show) {
        if (stickyBar.hasAttribute('hidden')) stickyBar.removeAttribute('hidden');
        stickyBar.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(function () { stickyBar.classList.add('is-visible'); });
        root.classList.add('stk-offer--sticky-open');
      } else {
        stickyBar.classList.remove('is-visible');
        stickyBar.setAttribute('aria-hidden', 'true');
        root.classList.remove('stk-offer--sticky-open');
      }
      adjustChatLauncher(show);
    }

    // JS fallback for the CSS body:has() chat-lift — covers cases where the
    // Shopify Inbox launcher uses a selector our CSS block didn't catch (new
    // launcher versions, third-party chat apps). Queries a broad set of
    // floating-button candidates that sit at viewport bottom-right and shifts
    // their inline `bottom` so they don't sit on top of the CTA. Stashes the
    // original value in a data attr and restores on hide. Skips desktop where
    // the bar is a centered pill (no bottom-right overlap to worry about).
    function adjustChatLauncher(open) {
      if (window.innerWidth > 600) return;
      var selectors = [
        '#ShopifyChat',
        '#shopify-chat',
        'shopify-chat',
        'shopify-chat-launcher',
        '[id*="shopify-chat"]',
        '[class*="chat-bubble"]',
        '[data-online-store-chat]',
        '[aria-label*="Open chat" i]',
        '[aria-label*="Chat with us" i]'
      ];
      selectors.forEach(function (sel) {
        var nodes;
        try { nodes = document.querySelectorAll(sel); } catch (e) { return; }
        Array.prototype.forEach.call(nodes, function (el) {
          if (open) {
            if (!el.dataset.stkChatLifted) {
              el.dataset.stkChatLifted = '1';
              el.dataset.stkChatOrigBottom = el.style.bottom || '';
              el.style.transition = 'bottom 220ms ease';
            }
            el.style.bottom = '5.25rem';
          } else if (el.dataset.stkChatLifted) {
            el.style.bottom = el.dataset.stkChatOrigBottom || '';
            delete el.dataset.stkChatLifted;
            delete el.dataset.stkChatOrigBottom;
          }
        });
      });
    }

    function refreshWhatYouGet() {
      if (!wygQtyLine || !state.selectedTier) return;
      wygQtyLine.textContent = state.selectedTier + ' premium vinyl stickers';
    }

    // ── Additional Instructions autosave — same pattern as the PDP. Saves
    //    to state.notes; passed to the bridge as spec.notes; surfaces on the
    //    order as the "Notes" line property. prefers-reduced-motion-safe is
    //    handled by the CSS (the spinner animation is gated on the media).
    if (notesEl) {
      var notesStatusText = notesStatusEl && notesStatusEl.querySelector('.stk-offer__notes-status-text');
      var notesTimer = null;
      function setNotesStatus(s) {
        if (!notesStatusEl) return;
        notesStatusEl.classList.toggle('is-typing', s === 'typing');
        notesStatusEl.classList.toggle('is-saved', s === 'saved');
        if (notesStatusText) {
          notesStatusText.textContent = s === 'typing' ? 'Saving…' : s === 'saved' ? 'Saved' : '';
        }
      }
      notesEl.addEventListener('input', function () {
        state.notes = notesEl.value;
        if (!state.notes.trim()) {
          clearTimeout(notesTimer);
          setNotesStatus('idle');
          return;
        }
        setNotesStatus('typing');
        clearTimeout(notesTimer);
        notesTimer = setTimeout(function () { setNotesStatus('saved'); }, 600);
      });
    }

    // ── Offer-mode tooltips — mirrors v1 builder's [data-stk-tip] pattern.
    //    Each [data-stk-offer-tip] button pairs with its own pop via
    //    aria-controls (cut-type tip + "How it works" tip both bind here).
    root.querySelectorAll('[data-stk-offer-tip]').forEach(function (tipBtn) {
      var popId = tipBtn.getAttribute('aria-controls');
      var tipPop = popId && document.getElementById(popId);
      if (!tipPop) return;
      var closeTip = function () { tipPop.hidden = true; tipBtn.setAttribute('aria-expanded', 'false'); };
      var openTip  = function () { tipPop.hidden = false; tipBtn.setAttribute('aria-expanded', 'true'); };
      tipBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (tipPop.hidden) openTip(); else closeTip();
      });
      document.addEventListener('click', function (e) {
        if (tipPop.hidden) return;
        if (e.target === tipBtn || tipPop.contains(e.target)) return;
        closeTip();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !tipPop.hidden) closeTip();
      });
    });

    function refreshCta() {
      var ready = !!state.artwork_filename && !!state.selectedTier;
      cta.disabled = !ready;
      cta.textContent = state.artwork_filename ? 'Place My Order' : 'Upload your artwork to continue';
      syncStickyBar();
    }

    fileInput.addEventListener('change', function () {
      var f = fileInput.files && fileInput.files[0];
      if (!f) return;
      handleFile(f);
    });

    function handleFile(file) {
      state.artwork_filename = file.name;
      state.artwork_file = file;
      if (filenameEl) filenameEl.textContent = file.name;
      if (uploadLabel) uploadLabel.style.display = 'none';
      if (uploadPreview) uploadPreview.style.display = '';
      if (thumbImg) { thumbImg.removeAttribute('src'); thumbImg.hidden = true; }
      if (thumbEmpty) thumbEmpty.hidden = false;
      refreshCta();
      if (previewController) {
        try { previewController.setFile(file); } catch (err) { console.warn('[stk-offer] preview setFile failed', err); }
        openPreviewModal(uploadLabel);
        return;
      }
      // No preview engine on this page (deal variant — the section omits both the
      // engine script and its host). Show a plain thumbnail of the uploaded FILE:
      // confirmation of what they attached, deliberately not a rendered sticker.
      // Suppressing the auto-preview is the whole point (brief §5) — the proof
      // email is where the artwork is judged.
      paintLocalThumbnail(file);
    }

    function paintLocalThumbnail(file) {
      if (!thumbImg || !thumbEmpty) return;
      releaseObjectUrl();
      // Vector uploads (PDF/AI/EPS/SVG) have nothing a browser can render inline —
      // they keep the plain "File attached" chip beside the filename.
      if (!file || String(file.type || '').indexOf('image/') !== 0) {
        thumbImg.hidden = true;
        thumbImg.removeAttribute('src');
        thumbEmpty.hidden = false;
        return;
      }
      try {
        var url = URL.createObjectURL(file);
        state.objectUrl = url;
        thumbImg.src = url;
        thumbImg.hidden = false;
        thumbEmpty.hidden = true;
        if (stickyThumb) { stickyThumb.src = url; stickyThumb.hidden = false; }
      } catch (err) {
        thumbImg.hidden = true;
        thumbImg.removeAttribute('src');
        thumbEmpty.hidden = false;
      }
    }

    function releaseObjectUrl() {
      if (!state.objectUrl) return;
      try { URL.revokeObjectURL(state.objectUrl); } catch (err) { /* no-op */ }
      state.objectUrl = null;
    }

    if (editBtn) {
      editBtn.addEventListener('click', function () {
        if (previewModal) { openPreviewModal(editBtn); return; }
        // Nothing to edit without a preview — the button is labelled "Replace" on
        // that variant and re-opens the picker, matching the free-sample page's
        // "Replace file" affordance.
        fileInput.click();
      });
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        state.artwork_filename = '';
        state.artwork_file = null;
        state.preview_meta = null;
        releaseObjectUrl();
        fileInput.value = '';
        if (filenameEl) filenameEl.textContent = '';
        if (uploadLabel) uploadLabel.style.display = '';
        if (uploadPreview) uploadPreview.style.display = 'none';
        if (thumbImg) { thumbImg.removeAttribute('src'); thumbImg.hidden = true; }
        if (thumbEmpty) thumbEmpty.hidden = true;
        if (stickyThumb) { stickyThumb.removeAttribute('src'); stickyThumb.hidden = true; }
        if (previewController) {
          try { previewController.clear(); } catch (err) { console.warn('[stk-offer] preview clear failed', err); }
        }
        refreshCta();
      });
    }

    // ── Preview modal open/close + thumb snapshot ──────────
    function openPreviewModal(returnFocusTo) {
      if (!previewModal) return;
      previewModalLastFocus = returnFocusTo || document.activeElement;
      previewModal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      previewModalOpen = true;
      syncStickyBar();
      if (previewModalClose) {
        requestAnimationFrame(function () { previewModalClose.focus(); });
      }
    }

    function closePreviewModal() {
      if (!previewModal) return;
      previewModal.style.display = 'none';
      document.body.style.overflow = '';
      previewModalOpen = false;
      paintColumnThumbnail();
      syncStickyBar();
      if (previewModalLastFocus && typeof previewModalLastFocus.focus === 'function') {
        try { previewModalLastFocus.focus(); } catch (e) { /* element may be detached */ }
      }
      previewModalLastFocus = null;
    }

    // toDataURL can throw on a tainted canvas — engine takes care to keep it
    // untainted, but catch defensively so a snapshot failure doesn't block
    // the modal close. Falls back to the "rendering…" placeholder. The same
    // snapshot also feeds the sticky bar's thumbnail (closes the "yes, this
    // is my design" loop on the proxy CTA).
    function paintColumnThumbnail() {
      if (!thumbImg || !thumbEmpty) return;
      var canvas = previewCanvas;
      var hasRendered = canvas && canvas.width > 0 && canvas.height > 0 && canvas.style.display !== 'none';
      if (!hasRendered) {
        thumbImg.hidden = true;
        thumbImg.removeAttribute('src');
        thumbEmpty.hidden = false;
        if (stickyThumb) { stickyThumb.hidden = true; stickyThumb.removeAttribute('src'); }
        return;
      }
      try {
        var dataUrl = canvas.toDataURL('image/png');
        thumbImg.src = dataUrl;
        thumbImg.hidden = false;
        thumbEmpty.hidden = true;
        if (stickyThumb) { stickyThumb.src = dataUrl; stickyThumb.hidden = false; }
      } catch (e) {
        thumbImg.hidden = true;
        thumbImg.removeAttribute('src');
        thumbEmpty.hidden = false;
        if (stickyThumb) { stickyThumb.hidden = true; stickyThumb.removeAttribute('src'); }
      }
    }

    if (previewModalClose) previewModalClose.addEventListener('click', closePreviewModal);
    if (previewModalDone)  previewModalDone.addEventListener('click', closePreviewModal);
    if (previewModal) {
      previewModal.addEventListener('click', function (e) {
        if (e.target === previewModal) closePreviewModal();
      });
    }

    // V6 — bypass the proof-email modal: CTA goes straight to checkout. The
    // artwork-present gate stays (refreshCta() flips disabled when state.
    // artwork_filename is empty). proof_email is intentionally NOT collected
    // here — the bridge treats it as optional and the proof flow resolves the
    // recipient from order.email (Shopify checkout collects the email there).
    // See briefs/sticker-offer-lp-v6-bypass-proof-modal-2026-06-22.md.
    cta.addEventListener('click', function () {
      if (cta.disabled || state.submitting) return;
      // Order bump (deal variant): the interstitial fires HERE, at Place My Order,
      // and accept/decline are each themselves the checkout action — there is no
      // third button to press afterwards, so the decision can't become a dead end.
      // Returns false on the 2″ offer page (no ladder, no modal), which keeps that
      // page going straight to checkout exactly as it does today.
      if (maybeOpenBump()) return;
      startCheckout(null);
    });

    // ── Order-bump interstitial ─────────────────────────────
    function maybeOpenBump() {
      if (!bumpModal || state.bumpDecided || !state.selectedTier) return false;
      var entry = bumpEntryFor(Number(state.selectedTier));
      if (!entry) return false;
      var now = computeOfferTotal();
      var next = computeOfferTotal(entry);
      if (!now || !next) return false;
      // The REAL delta for this customer's spec, not the config's bare add-on:
      // gloss and kiss-cut each compound onto the bumped base, so a hardcoded
      // "+$10.00" would be a number we don't charge the moment they pick a finish.
      var delta = round2(next.total - now.total);
      if (!(delta > 0)) return false;

      var from = Number(state.selectedTier);
      var ships = entry.toQty + entry.bonus;
      var moreStickers = ships - from;

      // Every figure below is DERIVED — quantities from the ladder, totals from the
      // same computeOfferTotal() the bridge mirrors, rates from those totals. A
      // literal here would survive a price change and start lying on an ad page.
      if (bumpHeadlineEl) bumpHeadlineEl.textContent = 'Make it ' + ships + ' stickers.';
      if (bumpSubLeadEl) bumpSubLeadEl.textContent = "You'd be paying for " + entry.toQty + ' — ';
      // Bolded half: the gift is the claim, so it carries the emphasis.
      if (bumpSubBoldEl) bumpSubBoldEl.textContent = 'the last ' + entry.bonus + ' are on us.';

      if (bumpNow.qty)   bumpNow.qty.textContent = from + ' stickers';
      if (bumpNow.note)  bumpNow.note.textContent = 'What you picked';
      if (bumpNow.total) bumpNow.total.textContent = '$' + now.total.toFixed(2);
      if (bumpNow.each)  bumpNow.each.textContent = perStickerLabel(now.total, from);

      if (bumpUp.qty)   bumpUp.qty.textContent = ships + ' stickers';
      if (bumpUp.note)  bumpUp.note.textContent = moreStickers + ' more, ' + entry.bonus + ' of them free';
      if (bumpUp.total) bumpUp.total.textContent = '$' + next.total.toFixed(2);
      if (bumpUp.each)  bumpUp.each.textContent = perStickerLabel(next.total, ships);
      // Only mention postage if it actually moves. With a single flat band it
      // doesn't, and saying so would be noise; if bands are added later this
      // starts telling the truth on its own rather than quoting a stale number.
      if (bumpShipEl) {
        var shipNow = resolveShipping(from);
        var shipNext = resolveShipping(ships);
        if (shipNow && shipNext && round2(shipNext.price) !== round2(shipNow.price)) {
          bumpShipEl.textContent = 'Shipping goes from $' + shipNow.price.toFixed(2)
            + ' to $' + shipNext.price.toFixed(2) + ' on the bigger pack.';
          bumpShipEl.hidden = false;
        } else {
          bumpShipEl.textContent = '';
          bumpShipEl.hidden = true;
        }
      }
      // States the thing AND the cost. "Yes — add 25 free" described only the bonus,
      // which reads slightly like a trick.
      if (bumpAcceptBtn) { bumpAcceptBtn.textContent = 'Add ' + moreStickers + ' stickers — $' + delta.toFixed(2); bumpAcceptBtn.disabled = false; }
      if (bumpDeclineBtn) { bumpDeclineBtn.textContent = 'No thanks, keep ' + from; bumpDeclineBtn.disabled = false; }
      if (bumpError) bumpError.textContent = '';

      state.pendingBump = entry;
      openBumpModal();
      return true;
    }

    function openBumpModal() {
      if (!bumpModal) return;
      bumpLastFocus = document.activeElement;
      bumpModal.hidden = false;
      document.body.style.overflow = 'hidden';
      bumpModalOpen = true;
      syncStickyBar();
      if (bumpAcceptBtn) requestAnimationFrame(function () { bumpAcceptBtn.focus(); });
    }

    function closeBumpModal() {
      if (!bumpModal) return;
      bumpModal.hidden = true;
      document.body.style.overflow = '';
      bumpModalOpen = false;
      syncStickyBar();
      if (bumpLastFocus && typeof bumpLastFocus.focus === 'function') {
        try { bumpLastFocus.focus(); } catch (e) { /* element may be detached */ }
      }
      bumpLastFocus = null;
    }

    // Dismissing (backdrop or Escape) submits NOTHING — but it does spend the
    // offer for this session, so the next Place My Order goes straight to
    // checkout. Re-presenting the same modal every time the customer taps the CTA
    // turns a one-time offer into a gate they have to get past. Mirrors the PDP's
    // bumpDeclinedThisSession.
    function dismissBump() {
      if (state.submitting) return;
      state.bumpDecided = true;
      state.pendingBump = null;
      closeBumpModal();
    }
    if (bumpModal) {
      bumpModal.addEventListener('click', function (e) {
        if (e.target === bumpModal) dismissBump();
      });
    }

    if (bumpAcceptBtn) {
      bumpAcceptBtn.addEventListener('click', function () {
        if (state.submitting) return;
        state.bumpDecided = true;
        state.bump = state.pendingBump || null;
        refreshWhatYouGet();
        refreshTotal();
        // The modal stays OPEN through the round-trip so a bridge failure surfaces
        // where the customer is looking; success redirects off the page anyway.
        startCheckout(bumpAcceptBtn);
      });
    }
    if (bumpDeclineBtn) {
      bumpDeclineBtn.addEventListener('click', function () {
        if (state.submitting) return;
        state.bumpDecided = true;
        state.bump = null;
        startCheckout(bumpDeclineBtn);
      });
    }

    // Shared checkout submit. The plain CTA passes null and behaves exactly as the
    // inline handler did before the bump existed; accept/decline pass their own
    // button so the label swap and any error land where the customer is looking.
    function startCheckout(triggerBtn) {
      if (state.submitting) return;
      state.submitting = true;
      var btn = triggerBtn || cta;
      var originalLabel = btn.textContent;
      cta.disabled = true;
      if (btn !== cta) btn.disabled = true;
      if (bumpAcceptBtn) bumpAcceptBtn.disabled = true;
      if (bumpDeclineBtn) bumpDeclineBtn.disabled = true;
      btn.textContent = 'Creating checkout…';
      if (ctaError) ctaError.textContent = '';
      if (bumpError) bumpError.textContent = '';
      syncStickyBar();
      submit().catch(function (err) {
        console.error('[stk-offer] submit failed', err);
        var msg = String((err && err.message) || 'Could not start checkout — please try again');
        if (btn !== cta && bumpError) bumpError.textContent = msg;
        else if (ctaError) ctaError.textContent = msg;
        state.submitting = false;
        btn.textContent = originalLabel;
        if (btn !== cta) btn.disabled = false;
        if (bumpAcceptBtn) bumpAcceptBtn.disabled = false;
        if (bumpDeclineBtn) bumpDeclineBtn.disabled = false;
        refreshCta();
      });
    }

    // Sticky bar proxy — fires the real cta's click so the V6 straight-to-
    // checkout submit() path is the single source of truth. The cta's own
    // double-submit guard (cta.disabled || state.submitting) makes this safe;
    // we still bail early if the bar is disabled to avoid a no-op click.
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        if (stickyBtn.disabled || cta.disabled || state.submitting) return;
        cta.click();
        syncStickyBar();
      });
    }

    // Hide the bar whenever the real cta is on screen so there are never two
    // CTAs visible at once. Falls back gracefully when IntersectionObserver
    // isn't supported (treat the cta as out-of-view → bar relies on artwork
    // + modal gating only).
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        ctaInView = entries[0].isIntersecting;
        syncStickyBar();
      }, { root: null, threshold: 0 });
      io.observe(cta);
    } else {
      ctaInView = false;
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (previewModal && previewModal.style.display === 'flex') closePreviewModal();
      // Escape never submits — it puts the customer back on the page with the
      // order unchanged, rather than pushing them into a checkout they didn't
      // confirm. It does spend the offer for this session (see dismissBump).
      if (bumpModal && !bumpModal.hidden) dismissBump();
    });

    function submit() {
      var offer = state.config && state.config[offerKey];
      if (!offer) return Promise.reject(new Error('Offer config unavailable — please reload the page'));
      if (!state.selectedTier) return Promise.reject(new Error('Please select a quantity'));
      var tier = offer.tiers[state.selectedTier];
      if (!tier) return Promise.reject(new Error('Invalid tier'));

      // V4 strict validation — mirror the bridge's options check exactly
      // (computeStickerOfferPrice returns null → 400 on any miss; we'd rather
      // reject client-side than round-trip a guaranteed 400).
      var shapeOpts = Array.isArray(offer.shape_options) ? offer.shape_options : [];
      if (shapeOpts.indexOf(state.selectedShape) < 0) return Promise.reject(new Error('Please pick a shape'));
      var cutOpts = Array.isArray(offer.cut_type_options) ? offer.cut_type_options : [];
      if (cutOpts.indexOf(state.selectedCutType) < 0) return Promise.reject(new Error('Please pick a cut type'));
      var finishOpts = Array.isArray(offer.finish_options) ? offer.finish_options : [];
      if (finishOpts.indexOf(state.selectedFinish) < 0) return Promise.reject(new Error('Please pick a finish'));
      var speedKeys = offer.production_speed ? Object.keys(offer.production_speed) : [];
      if (speedKeys.indexOf(state.selectedSpeed) < 0) return Promise.reject(new Error('Please pick a production speed'));
      var borderOpts = Array.isArray(offer.border_options) ? offer.border_options : [];
      if (borderOpts.indexOf(state.selectedBorder) < 0) return Promise.reject(new Error('Please pick a border type'));

      // The config block's own key IS the spec flag the bridge branches on
      // (spec.offer_promo / spec.deal_100_offer), so there is one string to keep in
      // step across page, config and bridge. Assigned first so the signed payload's
      // key order is unchanged from before this variant existed.
      var spec = {};
      spec[offerKey] = true;
      spec.product_type = offer.product_type;
      spec.size = offer.size;
      spec.shape = state.selectedShape;
      spec.cut_type = state.selectedCutType;
      spec.material = state.selectedFinish;
      spec.production_speed = state.selectedSpeed;
      spec.border_type = state.selectedBorder;
      // PRICE THE TIER, SHIP THE BONUS. An accepted bump moves the order onto the
      // ladder's to_qty — a real rung — and the extras ride as a fulfilment bonus
      // the bridge derives from config. 110 and 225 are never sent as a quantity:
      // they aren't matrix quantities and pricing them would fail closed.
      spec.qty = state.bump ? Number(state.bump.toQty) : Number(state.selectedTier);
      spec.artwork_filename = state.artwork_filename;
      spec.notes = state.notes || '';
      // Appended LAST so an unbumped order's signed payload is byte-unchanged.
      if (state.bump) spec.bump_from = Number(state.bump.from);
      var payload = {
        product_type: offer.product_type,
        variant_id: variantId,
        quantity: 1,
        spec: spec
      };

      // Capture the original file in Cloudinary BEFORE signing. Failure here
      // sets an upload_status flag instead of blocking checkout — the bridge
      // surfaces "UPLOAD FAILED" on the order so ops can request the file.
      // The engine's uploader when the engine is loaded (2″ offer page — unchanged),
      // the inlined one when it isn't (deal variant suppresses the preview engine).
      var uploadArtwork = (window.TDStickerPreview && window.TDStickerPreview.uploadArtwork)
        ? function (f) { return window.TDStickerPreview.uploadArtwork(f, { folder: 'orders' }); }
        : function (f) { return uploadToCloudinary(f, 'orders'); };
      var uploadPromise;
      if (state.artwork_file) {
        uploadPromise = uploadArtwork(state.artwork_file)
          .then(function (up) {
            spec.artwork_url = up.secure_url;
            spec.artwork_public_id = up.public_id;
          })
          .catch(function (err) {
            console.warn('[stk-offer] order-time artwork upload failed', err);
            spec.artwork_upload_status = 'failed';
          });
      } else {
        uploadPromise = Promise.resolve();
      }

      return uploadPromise.then(function () { return computeHmacHex(payload, nonce); }).then(function (signature) {
        return fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_type: payload.product_type,
            variant_id: payload.variant_id,
            quantity: payload.quantity,
            spec: payload.spec,
            nonce: nonce,
            signature: signature
          })
        });
      }).then(function (res) {
        return res.json().then(function (data) { return { res: res, data: data }; });
      }).then(function (out) {
        if (!out.res.ok || !out.data.invoiceUrl) throw new Error(out.data.error || ('HTTP ' + out.res.status));
        window.location.assign(out.data.invoiceUrl);
      });
    }

    function computeHmacHex(payload, nonceHex) {
      var enc = new TextEncoder();
      return crypto.subtle.importKey('raw', enc.encode(nonceHex), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
        .then(function (key) { return crypto.subtle.sign('HMAC', key, enc.encode(JSON.stringify(payload))); })
        .then(function (sig) { return Array.from(new Uint8Array(sig)).map(function (b) { return b.toString(16).padStart(2, '0'); }).join(''); });
    }

    root.dataset.stkOfferReady = '1';
  }

  function initAll(root) {
    var scope = root || document;
    var galleries = scope.querySelectorAll('[data-stk-gallery]');
    galleries.forEach(initGallery);
    var offerRoot = (scope.matches && scope.matches('[data-stk-offer]')) ? scope : scope.querySelector('[data-stk-offer]');
    if (offerRoot) initOffer(offerRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-init when a section is added/reordered/changed.
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) initAll(e.target);
  });
})();
